import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { setCollegeInfo } from '../utils/receiptHeaderUtils';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.timeout = 30000; // 30 second timeout

// Session timeout settings (in milliseconds)
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity
const WARNING_BEFORE_LOGOUT = 5 * 60 * 1000; // Show warning 5 minutes before logout

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const navigate = useNavigate();
  
  // Ref to track if login is in progress - prevents race condition
  const isLoggingIn = useRef(false);
  const inactivityTimer = useRef(null);
  const warningTimer = useRef(null);
  const lastActivity = useRef(Date.now());

  // Reset inactivity timer on user activity
  const resetInactivityTimer = useCallback(() => {
    lastActivity.current = Date.now();
    setShowSessionWarning(false);
    
    // Clear existing timers
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    
    // Only set timers if user is logged in
    if (token && user) {
      // Set warning timer
      warningTimer.current = setTimeout(() => {
        setShowSessionWarning(true);
      }, INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT);
      
      // Set logout timer
      inactivityTimer.current = setTimeout(() => {
        console.log('Session expired due to inactivity');
        logout(true); // true = session expired
      }, INACTIVITY_TIMEOUT);
    }
  }, [token, user]);

  // Track user activity
  useEffect(() => {
    if (token && user) {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      
      const handleActivity = () => {
        resetInactivityTimer();
      };
      
      events.forEach(event => {
        document.addEventListener(event, handleActivity);
      });
      
      // Initial timer setup
      resetInactivityTimer();
      
      return () => {
        events.forEach(event => {
          document.removeEventListener(event, handleActivity);
        });
        if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        if (warningTimer.current) clearTimeout(warningTimer.current);
      };
    }
  }, [token, user, resetInactivityTimer]);

  // Store login timestamp for session tracking
  useEffect(() => {
    if (token && !localStorage.getItem('loginTime')) {
      localStorage.setItem('loginTime', Date.now().toString());
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Only verify token on initial load, not during active login
      if (!isLoggingIn.current) {
        verifyToken();
      } else {
        // Reset the flag and stop loading since user is already set during login
        isLoggingIn.current = false;
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Fetch and cache college info if not already cached
      const userData = response.data;
      if ((userData.role === 'Admin' || userData.role === 'Staff' || userData.role === 'Student') && !localStorage.getItem('collegeInfo')) {
        try {
          const collegeResponse = await axios.get(`${API}/tenants/my-college/settings`);
          if (collegeResponse.data) {
            const collegeInfo = {
              ...collegeResponse.data,
              college_name: collegeResponse.data.name || collegeResponse.data.college_name,
              logo_url: collegeResponse.data.logo || collegeResponse.data.logo_url
            };
            setCollegeInfo(collegeInfo);
          }
        } catch (collegeError) {
          console.log('Could not fetch college info on verify:', collegeError);
        }
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      // Only logout if token is actually invalid (401 error), not for network errors
      if (!isLoggingIn.current && error?.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      // Set flag to prevent race condition with verifyToken
      isLoggingIn.current = true;
      
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      setToken(access_token);
      setLoading(false);
      
      // Fetch and cache college info for receipts after successful login
      // Only for Admin, Staff roles (not SuperAdmin/GroupAdmin)
      if (userData.role === 'Admin' || userData.role === 'Staff' || userData.role === 'Student') {
        try {
          const collegeResponse = await axios.get(`${API}/tenants/my-college/settings`);
          if (collegeResponse.data) {
            const collegeInfo = {
              ...collegeResponse.data,
              college_name: collegeResponse.data.name || collegeResponse.data.college_name,
              logo_url: collegeResponse.data.logo || collegeResponse.data.logo_url
            };
            setCollegeInfo(collegeInfo);
          }
        } catch (collegeError) {
          console.log('Could not fetch college info:', collegeError);
        }
        
        // Fetch and cache timezone/date-time settings
        try {
          const tzResponse = await axios.get(`${API}/master-settings/timezone-settings`);
          if (tzResponse.data) {
            localStorage.setItem('timezoneSettings', JSON.stringify(tzResponse.data));
          }
        } catch (tzError) {
          console.log('Could not fetch timezone settings:', tzError);
        }
      }
      
      // Check if user needs to change password (first login)
      if (userData.require_password_change) {
        navigate('/force-password-change');
      } else if (userData.role === 'GroupAdmin') {
        // GroupAdmin goes to group dashboard
        navigate('/group/dashboard');
      } else {
        navigate('/dashboard');
      }
      
      return { success: true };
    } catch (error) {
      isLoggingIn.current = false;
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  const logout = (sessionExpired = false) => {
    // Get tenant code before clearing localStorage
    const tenantCode = localStorage.getItem('tenant_code');
    
    // Clear all auth related data
    localStorage.removeItem('token');
    localStorage.removeItem('tenant_code');
    localStorage.removeItem('collegeInfo');
    localStorage.removeItem('loginTime');
    localStorage.removeItem('timezoneSettings');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setShowSessionWarning(false);
    
    // Clear timers
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    
    // Use React Router navigate with replace option for SPA navigation
    // Small delay ensures state updates complete before navigation
    setTimeout(() => {
      // Redirect to tenant-specific login if tenant code exists, otherwise main login
      const loginPath = tenantCode ? `/${tenantCode}/login` : '/login';
      const state = sessionExpired ? { sessionExpired: true } : {};
      navigate(loginPath, { replace: true, state });
    }, 100);
  };

  // Extend session (called when user clicks "Stay Logged In")
  const extendSession = () => {
    resetInactivityTimer();
    setShowSessionWarning(false);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    refreshUser,
    extendSession,
    showSessionWarning,
    isAuthenticated: !!token,
    isAdmin: user?.role === 'Admin',
    isStaff: user?.role === 'Staff',
    isStudent: user?.role === 'Student',
    axios
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
